import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SID_KEY = "anabiya:chat_sid";
const NAME_KEY = "anabiya:chat_name";
const EMAIL_KEY = "anabiya:chat_email";
const POLL_MS = 4000;

const fmt = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

export const LiveChat = () => {
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState(() => localStorage.getItem(SID_KEY) || "");
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) || "");
  const [email, setEmail] = useState(() => localStorage.getItem(EMAIL_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef(null);
  const started = !!sid;

  // Poll for new messages while a session exists
  useEffect(() => {
    if (!sid) return;
    let cancelled = false;
    let lastCount = messages.length;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/chat/${sid}`);
        if (cancelled) return;
        const msgs = data.messages || [];
        setMessages(msgs);
        const newAdmin = msgs.filter((m) => m.from === "admin").length;
        const prevAdmin = lastCount ? messages.filter((m) => m.from === "admin").length : 0;
        if (!open && newAdmin > prevAdmin) setUnread((u) => u + (newAdmin - prevAdmin));
        lastCount = msgs.length;
      } catch (e) { /* ignore transient */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid, open]);

  useEffect(() => {
    if (open) setUnread(0);
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const canStart = name.trim().length > 0 && input.trim().length > 0;

  const start = async () => {
    if (!canStart || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat/start`, {
        name: name.trim(),
        email: email.trim() || undefined,
        message: input.trim(),
        page_url: window.location.pathname + window.location.search,
      });
      localStorage.setItem(SID_KEY, data.session_id);
      localStorage.setItem(NAME_KEY, name.trim());
      if (email.trim()) localStorage.setItem(EMAIL_KEY, email.trim());
      setSid(data.session_id);
      setInput("");
    } catch (e) {
      // silent; user can retry
    } finally { setSending(false); }
  };

  const send = async () => {
    if (!input.trim() || sending || !sid) return;
    const text = input.trim();
    setInput("");
    // Optimistic append
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, from: "customer", text, at: new Date().toISOString(), _optimistic: true }]);
    try {
      await axios.post(`${API}/chat/append`, { session_id: sid, message: text });
    } catch (e) { /* keep optimistic; will reconcile on next poll */ }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      started ? send() : start();
    }
  };

  const brandGreet = useMemo(
    () => "Hello — this is Anabiya's studio desk in Mumbai. Tell us what you're building, we usually reply in a few minutes.",
    []
  );

  return (
    <>
      {/* Bubble */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="livechat-bubble"
        aria-label="Live chat"
        className="fixed bottom-24 right-6 z-[45] w-14 h-14 rounded-full bg-forest text-white shadow-2xl flex items-center justify-center hover:scale-105 transition-transform"
      >
        <MessageCircle size={22} strokeWidth={1.4} />
        {unread > 0 && (
          <span data-testid="livechat-unread" className="absolute -top-1 -right-1 bg-champagne text-forest text-[10px] font-mono rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.7, 0, 0.2, 1] }}
            data-testid="livechat-panel"
            className="fixed bottom-24 right-6 md:right-24 z-[50] w-[360px] max-w-[92vw] h-[520px] bg-white border border-hairline shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-forest text-white px-5 py-4 flex items-center justify-between">
              <div>
                <div className="overline text-champagne">§ Live Studio</div>
                <div className="font-display text-xl leading-none mt-1">
                  Anabiya <span className="font-editorial italic text-champagne">desk.</span>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="livechat-close"
                className="text-white/70 hover:text-white"
                aria-label="Close"
              >
                <X size={18} strokeWidth={1.4} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 bg-cream space-y-3">
              <ChatBubble from="admin" text={brandGreet} at={new Date().toISOString()} />
              {messages.map((m) => (
                <ChatBubble key={m.id} from={m.from} text={m.text} at={m.at} />
              ))}
            </div>

            {/* Input */}
            {!started ? (
              <div className="p-4 border-t border-hairline bg-white space-y-2">
                <input
                  data-testid="livechat-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full bg-transparent border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest"
                />
                <input
                  data-testid="livechat-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email (optional)"
                  type="email"
                  className="w-full bg-transparent border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest"
                />
                <div className="flex items-center gap-2">
                  <input
                    data-testid="livechat-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKey}
                    placeholder="Tell us what you're looking for…"
                    className="flex-1 bg-transparent border border-hairline rounded-full px-4 py-2 text-sm text-forest focus:outline-none focus:border-forest"
                  />
                  <button
                    data-testid="livechat-start"
                    onClick={start}
                    disabled={!canStart || sending}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-forest text-white disabled:opacity-40"
                    aria-label="Send"
                  >
                    <ArrowRight size={15} strokeWidth={1.8} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-3 border-t border-hairline bg-white flex items-center gap-2">
                <input
                  data-testid="livechat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder="Type a message…"
                  className="flex-1 bg-transparent border border-hairline rounded-full px-4 py-2 text-sm text-forest focus:outline-none focus:border-forest"
                />
                <button
                  data-testid="livechat-send"
                  onClick={send}
                  disabled={!input.trim() || sending}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-forest text-white disabled:opacity-40"
                  aria-label="Send"
                >
                  <Send size={14} strokeWidth={1.8} />
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const ChatBubble = ({ from, text, at }) => {
  const isAdmin = from === "admin";
  return (
    <div className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}>
      <div
        className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm leading-snug ${
          isAdmin
            ? "bg-white border border-hairline text-forest rounded-tl-sm"
            : "bg-forest text-white rounded-tr-sm"
        }`}
      >
        <div className="whitespace-pre-wrap">{text}</div>
        <div className={`overline text-[9px] mt-1 ${isAdmin ? "text-forest/40" : "text-white/50"}`}>{fmt(at)}</div>
      </div>
    </div>
  );
};
